import { useEffect, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Upload, Loader2, FileText } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Modal } from "@/components/ui/customs/modal";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  useCreateFile,
  useCreateFileBo,
  useDeleteFileByIdBo,
  useSendFileViaApiBo,
} from "@/api/file.api";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import {
  CreateFileBoDto,
  CreateFileDto,
  GetOneExpeditionBoResponseDto,
} from "@/types/api/api-types";
import { ExpeditionProcessStatusEnum } from "@/constants/enums/expeditionProcessStatus.enum";
import { PRE_FINALIZATION_STATUSES } from "@/constants/enums/expeditionProcessStatus.enum";
import { useFileTypeLabels } from "@/hooks/useEnumLabels";

const DEFAULT_UPLOAD_FILE_TYPES: FileTypeEnum[] = [
  FileTypeEnum.EXPEDITION_CUSTOM_INVOICE,
  FileTypeEnum.EXPEDITION_CUSTOM_INVOICE_EXCEL,
  FileTypeEnum.EXPEDITION_DAE,
  FileTypeEnum.EXPEDITION_SHIPPING_LABEL,
  FileTypeEnum.EXPEDITION_INVOICE,
  FileTypeEnum.EXPEDITION_FORM,
  FileTypeEnum.EXPEDITION_COMPLEMENTARY_INVOICE,
  FileTypeEnum.EXPEDITION_IMPORT_LICENSE,
  FileTypeEnum.EXPEDITION_CERTIFICATE_CHAMPAGNE,
  FileTypeEnum.EXPEDITION_OTHER,
  FileTypeEnum.IMPORTER_LICENCE,
  FileTypeEnum.IMPORTER_BASIC_PERMIT,
  FileTypeEnum.IMPORTER_OTHER,
  FileTypeEnum.PRODUCT_COLA,
  FileTypeEnum.OTHER,
];

type UploadFileExpedition = Pick<GetOneExpeditionBoResponseDto, "id" | "processStatus">;

type UploadFileModalBaseProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  expeditionDocumentsId?: string;
  expeditionId?: string;
  allowedFileTypes?: FileTypeEnum[];
  accept?: string[];
  invalidateQueryKey?: unknown[];
  createFile: (
    payload: CreateFileBoDto | CreateFileDto,
  ) => Promise<{ uploadUrl: string; fileId: string }>;
  afterS3Upload?: (fileId: string, options: { sendViaApi: boolean }) => Promise<void>;
  showBoOptions?: boolean;
  uploadSuccessCallback?: (fileId: string, selectedType: string, file: File) => void;
  submitOnDrop?: boolean;
  disabledOptions?: {
    isPrivate?: boolean;
    sendViaApi?: boolean;
  };
  defaultOptions?: {
    isPrivate?: boolean;
    sendViaApi?: boolean;
  };
  expedition?: UploadFileExpedition;
};

const UploadFileModalBase = ({
  open,
  onOpenChange,
  expeditionDocumentsId,
  expeditionId,
  allowedFileTypes = DEFAULT_UPLOAD_FILE_TYPES,
  accept,
  invalidateQueryKey,
  createFile,
  afterS3Upload,
  showBoOptions = false,
  uploadSuccessCallback,
  submitOnDrop = false,
  disabledOptions = {
    isPrivate: false,
    sendViaApi: false,
  },
  defaultOptions = {
    isPrivate: false,
    sendViaApi: false,
  },
  expedition,
}: UploadFileModalBaseProps) => {
  const { t } = useTranslation(["boExpeditionForm", "common"]);
  const fileTypeLabels = useFileTypeLabels();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const defaultIsPrivate = defaultOptions.isPrivate ?? false;
  const defaultSendViaApi = defaultOptions.sendViaApi ?? false;
  const fileTypeSelectorNeeded = allowedFileTypes.length > 1;
  const singleAllowedType = fileTypeSelectorNeeded ? null : allowedFileTypes[0];

  const [selectedType, setSelectedType] = useState<FileTypeEnum | null>(singleAllowedType);
  const [isPrivate, setIsPrivate] = useState(defaultIsPrivate);
  const [sendViaApi, setSendViaApi] = useState(defaultSendViaApi);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    if (open) {
      setIsPrivate(defaultIsPrivate);
      setSendViaApi(defaultSendViaApi);
      setSelectedType(singleAllowedType);
    }
  }, [open, defaultIsPrivate, defaultSendViaApi, singleAllowedType]);

  const resetForm = () => {
    setSelectedType(singleAllowedType);
    setIsPrivate(defaultIsPrivate);
    setSendViaApi(defaultSendViaApi);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const isAccepted = (file: File) => {
    if (!accept || accept.length === 0) return true;
    const name = file.name.toLowerCase();
    return accept.some((a) => {
      const rule = a.trim().toLowerCase();
      if (rule.startsWith(".")) return name.endsWith(rule);
      if (rule.endsWith("/*")) return file.type.startsWith(rule.slice(0, -1));
      return file.type === rule;
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isAccepted(file)) {
      toast.error(t("uploadFileModal.fileTypeNotAllowed"));
      e.target.value = "";
      return;
    }
    setSelectedFile(file);
  };

  useEffect(() => {
    if (submitOnDrop && selectedFile && selectedType && !isPending) {
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile]);

  const handleSubmit = async () => {
    if (!selectedFile || !selectedType) return;

    setIsPending(true);
    try {
      const payload: CreateFileBoDto | CreateFileDto = {
        fileName: selectedFile.name,
        type: selectedType,
        contentType: selectedFile.type,
        expeditionDocumentsId,
        // Only the BO endpoint accepts these fields, the client one rejects unknown properties
        ...(showBoOptions && { isPrivate, sendViaApi }),
      };

      const { uploadUrl, fileId } = await createFile(payload);

      await fetch(uploadUrl, {
        method: "PUT",
        body: selectedFile,
        headers: { "Content-Type": selectedFile.type },
      });

      if (afterS3Upload) {
        await afterS3Upload(fileId, { sendViaApi });
      } else {
        toast.success(t("uploadFileModal.uploadSuccess"));
      }

      if (expeditionId) {
        queryClient.invalidateQueries({
          queryKey: invalidateQueryKey ?? ["expedition", "bo", expeditionId],
        });
      }

      if (uploadSuccessCallback) {
        uploadSuccessCallback(fileId, selectedType, selectedFile);
      }

      resetForm();
      onOpenChange(false);
    } catch {
      toast.error(t("uploadFileModal.uploadError"));
    } finally {
      setIsPending(false);
    }
  };

  const handleOpenChange = (value: boolean) => {
    if (!value) resetForm();
    onOpenChange(value);
  };

  const canSubmit = !!selectedType && !!selectedFile;

  const expeditionProcessStatus = expedition?.processStatus as ExpeditionProcessStatusEnum;

  const isPreFinalization = PRE_FINALIZATION_STATUSES.includes(expeditionProcessStatus);

  const handleClose = () => handleOpenChange(false);

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        title={t("uploadFileModal.title")}
        description={t("uploadFileModal.description")}
        size="sm"
        contentClassName="sm:max-w-md"
        footer={
          <>
            <Button variant="outline" type="button" disabled={isPending} onClick={handleClose}>
              {t("common:actions.cancel")}
            </Button>
            <Button type="button" disabled={!canSubmit || isPending} onClick={handleSubmit}>
              {isPending ? (
                <Loader2 className="mr-2 size-4 animate-spin" />
              ) : (
                <div className="flex items-center gap-2">
                  <Upload className="mr-2 size-4" />
                  {t("uploadFileModal.submit")}
                </div>
              )}
            </Button>
          </>
        }
      >
        <div className="flex min-w-0 flex-col gap-4">
          {fileTypeSelectorNeeded && (
            <Select
              value={selectedType ?? undefined}
              onValueChange={(v) => setSelectedType(v as FileTypeEnum)}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t("uploadFileModal.fileTypePlaceholder")} />
              </SelectTrigger>
              <SelectContent>
                {allowedFileTypes.map((ft) => (
                  <SelectItem key={ft} value={ft}>
                    {fileTypeLabels[ft]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="flex items-center gap-6">
            {!disabledOptions.isPrivate && (
              <div className="flex items-center gap-2">
                <Switch checked={isPrivate} onCheckedChange={setIsPrivate} />
                <label className="text-sm text-gray-700">{t("uploadFileModal.privateLabel")}</label>
              </div>
            )}
            {!disabledOptions.sendViaApi && (
              <div className="flex items-center gap-2">
                <Switch checked={sendViaApi} onCheckedChange={setSendViaApi} />
                <label className="text-sm text-gray-700">
                  {t("uploadFileModal.sendViaApiLabel")}
                </label>
              </div>
            )}
          </div>
          {sendViaApi &&
            (isPreFinalization ? (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">
                  {t("uploadFileModal.preFinalizationNotice")}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">
                  <Trans
                    t={t}
                    i18nKey="uploadFileModal.postFinalizationNotice"
                    components={{ br: <br /> }}
                  />
                </span>
              </div>
            ))}

          {selectedFile ? (
            <div className="flex items-center gap-3 overflow-hidden rounded-lg border bg-gray-50 p-3">
              <FileText className="size-5 shrink-0 text-gray-500" />
              <p className="min-w-0 flex-1 truncate text-sm font-medium text-gray-800">
                {selectedFile.name}
              </p>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => {
                  setSelectedFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
              >
                {t("uploadFileModal.changeFile")}
              </Button>
            </div>
          ) : (
            <div
              className={cn(
                "flex w-full cursor-pointer items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors",
                selectedType
                  ? "bg-brand50 border-brand700 hover:opacity-80"
                  : "border-gray-300 bg-gray-50",
              )}
              onClick={() => selectedType && fileInputRef.current?.click()}
            >
              <div className="flex flex-col items-center gap-2">
                <Upload
                  className={cn("size-6", selectedType ? "text-brand700" : "text-gray-400")}
                />
                <span
                  className={cn(
                    "text-center text-sm font-medium",
                    selectedType ? "text-brand700" : "text-gray-400",
                  )}
                >
                  {selectedType
                    ? t("uploadFileModal.selectFile")
                    : t("uploadFileModal.selectTypeFirst")}
                </span>
              </div>
            </div>
          )}
        </div>
      </Modal>

      <input
        ref={fileInputRef}
        type="file"
        accept={accept?.join(",")}
        className="hidden"
        onChange={handleFileSelect}
      />
    </>
  );
};

type UploadFileBoModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  expeditionDocumentsId?: string;
  expeditionId: string;
  allowedFileTypes?: FileTypeEnum[];
  accept?: string[];
  invalidateQueryKey?: unknown[];
  submitOnDrop?: boolean;
  disabledOptions?: {
    isPrivate?: boolean;
    sendViaApi?: boolean;
  };
  defaultOptions?: {
    isPrivate?: boolean;
    sendViaApi?: boolean;
  };
  expedition: UploadFileExpedition;
};

export const UploadFileBoModal = ({
  disabledOptions,
  defaultOptions,
  expedition,
  ...props
}: UploadFileBoModalProps) => {
  const { t } = useTranslation("boExpeditionForm");
  const { mutateAsync: createFileBo } = useCreateFileBo();
  const { mutateAsync: sendFileViaApiBo } = useSendFileViaApiBo();
  const { mutateAsync: deleteFileBo } = useDeleteFileByIdBo();

  const uploadFile = async (payload: CreateFileBoDto) => {
    const { uploadUrl, fileId } = await createFileBo(payload);
    return { uploadUrl, fileId };
  };

  const afterS3Upload = async (fileId: string, { sendViaApi }: { sendViaApi: boolean }) => {
    const expeditionProcessStatus = expedition.processStatus as ExpeditionProcessStatusEnum;
    const isPreFinalization = PRE_FINALIZATION_STATUSES.includes(expeditionProcessStatus);

    if (sendViaApi && !isPreFinalization) {
      try {
        await sendFileViaApiBo({ id: fileId, expeditionId: expedition.id });
        toast.success(t("uploadFileModal.uploadAndSendSuccess"));
      } catch {
        await deleteFileBo(fileId);
      }
    } else {
      toast.success(t("uploadFileModal.uploadSuccess"));
    }
  };

  return (
    <UploadFileModalBase
      {...props}
      createFile={uploadFile}
      afterS3Upload={afterS3Upload}
      showBoOptions
      disabledOptions={disabledOptions}
      defaultOptions={defaultOptions}
      expedition={expedition}
    />
  );
};

type UploadFileClientModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  expeditionDocumentsId?: string;
  expeditionId?: string;
  allowedFileTypes?: FileTypeEnum[];
  accept?: string[];
  invalidateQueryKey?: unknown[];
  submitOnDrop?: boolean;
  uploadSuccessCallback?: (fileId: string, selectedType: string, file: File) => void;
  defaultOptions?: {
    isPrivate?: boolean;
    sendViaApi?: boolean;
  };
};

export const UploadFileClientModal = ({ defaultOptions, ...props }: UploadFileClientModalProps) => {
  const { mutateAsync } = useCreateFile();

  return (
    <UploadFileModalBase
      {...props}
      createFile={mutateAsync}
      disabledOptions={{
        isPrivate: true,
        sendViaApi: true,
      }}
      defaultOptions={defaultOptions}
    />
  );
};
